import { Link } from 'react-router-dom';
import { articles } from '@/mocks/articles';
import { categories } from '@/mocks/categories';
import { fmtNum, tagClass } from '@/utils/helpers';

const featured = articles.find((a) => a.featured) || articles[0];
const quick = articles.filter((a) => a.id !== featured.id).slice(0, 5);
const hot = [...articles].sort((a, b) => b.reads - a.reads).slice(0, 6);

export default function HeroAndHot() {
  const fcat = categories.find((c) => c.slug === featured.cat);
  return (
    <>
      {/* featured banner */}
      <section className="mx-auto w-full max-w-6xl px-4 md:px-6 pt-6 md:pt-10">
        <div className="mb-4 flex items-center gap-3 text-xs text-foreground-500">
          <span className="inline-flex items-center gap-1.5 font-label font-semibold uppercase tracking-wider text-primary-600">
            <i className="ri-fire-fill" aria-hidden="true" /> 今日瓜田
          </span>
          <span>{new Date().getMonth() + 1}月{new Date().getDate()}日更新</span>
          <span className="h-px flex-1 bg-background-200" />
        </div>

        <div className="relative overflow-hidden rounded-lg">
          <img
            src={featured.cover}
            alt={featured.alt}
            title={`${featured.title} - 每日大瓜`}
            className="h-[420px] md:h-[500px] w-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-background-950/85 via-background-950/25 to-transparent" />
          <div className="absolute inset-x-0 bottom-0 p-5 md:p-8">
            <div className="flex flex-wrap items-center gap-2">
              <span className={`inline-flex items-center rounded-md border px-2 py-0.5 text-xs font-medium ${tagClass(featured.tag)}`}>
                <i className="ri-flashlight-fill mr-1 text-sm" aria-hidden="true" /> 头条热瓜 · {featured.tag}
              </span>
              <span className="inline-flex items-center gap-1 rounded-full bg-background-950/40 px-2.5 py-1 text-xs text-background-100 backdrop-blur">
                <i className={fcat?.icon} aria-hidden="true" /> {fcat?.name}
              </span>
            </div>
            <h2 className="mt-3 max-w-3xl text-xl md:text-3xl lg:text-4xl font-bold leading-snug text-background-50">
              <Link to={`/post/${featured.id}`} className="transition hover:text-primary-200">
                {featured.title}
              </Link>
            </h2>
            <p className="mt-3 hidden max-w-2xl text-sm leading-relaxed text-background-200 md:block">{featured.summary}</p>
            <div className="mt-4 flex flex-wrap items-center gap-3">
              <Link
                to={`/post/${featured.id}`}
                className="inline-flex items-center whitespace-nowrap rounded-md bg-primary-500 px-4 py-2.5 text-sm font-semibold text-background-50 transition hover:bg-primary-600"
              >
                进入吃瓜时间线 <i className="ri-arrow-right-line ml-1.5" aria-hidden="true" />
              </Link>
              <span className="inline-flex items-center gap-2 text-xs text-background-200">
                <i className="ri-eye-line" aria-hidden="true" /> {fmtNum(featured.reads)}热度
                <span className="mx-1 opacity-40">·</span>
                <i className="ri-chat-3-line" aria-hidden="true" /> {fmtNum(featured.comments)}讨论
              </span>
            </div>
          </div>
        </div>
      </section>

      <section className="mx-auto grid w-full max-w-6xl gap-6 px-4 md:px-6 py-8 md:py-12 lg:grid-cols-3">
        {/* quick feed */}
        <div className="lg:col-span-2">
          <div className="mb-4 flex items-center gap-2">
            <span className="flex h-8 w-8 items-center justify-center rounded-md bg-accent-100 text-accent-800"><i className="ri-timer-2-line" /></span>
            <h3 className="text-lg md:text-xl font-bold">今日速览</h3>
            <span className="text-xs text-foreground-400">· 即时瓜讯 · 持续更新</span>
          </div>
          <div className="divide-y divide-background-200 rounded-lg border border-background-200 bg-background-50">
            {quick.map((a, idx) => {
              const cat = categories.find((c) => c.slug === a.cat);
              return (
                <Link
                  key={a.id}
                  to={`/post/${a.id}`}
                  className="group flex items-start gap-4 p-4 transition hover:bg-background-100"
                >
                  <span className="font-label text-2xl font-bold leading-none text-background-300 group-hover:text-primary-400">
                    {String(idx + 1).padStart(2, '0')}
                  </span>
                  <div className="min-w-0 flex-1">
                    <p className="line-clamp-2 text-sm md:text-base font-semibold leading-snug text-foreground-950 group-hover:text-primary-700">
                      {a.title}
                    </p>
                    <p className="mt-1 flex items-center gap-2 text-xs text-foreground-500">
                      <span className="inline-flex items-center gap-1"><i className={cat?.icon} aria-hidden="true" />{cat?.name}</span>
                      <span className="inline-flex items-center gap-1 text-primary-600"><i className="ri-time-line" aria-hidden="true" />{a.date}</span>
                      <span className="hidden items-center gap-1 sm:inline-flex"><i className="ri-eye-line" aria-hidden="true" />{fmtNum(a.reads)}</span>
                    </p>
                  </div>
                  <i className="ri-arrow-right-up-line mt-1 text-foreground-300 transition group-hover:text-primary-600" aria-hidden="true" />
                </Link>
              );
            })}
          </div>
        </div>

        {/* hot board */}
        <aside>
          <div className="rounded-lg border border-background-200 bg-background-50 p-4 md:p-5">
            <div className="mb-3 flex items-center justify-between">
              <h3 className="flex items-center gap-2 text-lg font-bold">
                <span className="text-lg text-primary-500"><i className="ri-fire-fill" /></span> 全网热搜榜
              </h3>
              <span className="rounded-full bg-primary-50 px-2 py-0.5 text-xs text-primary-700">实时热度</span>
            </div>
            <ol className="space-y-1">
              {hot.map((a, i) => (
                <li key={a.id}>
                  <Link to={`/post/${a.id}`} className="group flex items-center gap-3 rounded-md px-2 py-2 transition hover:bg-background-100">
                    <span
                      className={`w-6 shrink-0 text-center font-label text-base font-bold ${
                        i < 3 ? 'text-primary-600' : 'text-foreground-400'
                      }`}
                    >
                      {i + 1}
                    </span>
                    <div className="min-w-0 flex-1">
                      <p className="truncate text-sm font-medium text-foreground-800 group-hover:text-primary-700">{a.title}</p>
                      <p className="text-[11px] text-foreground-400">热度 {fmtNum(a.reads)}</p>
                    </div>
                    <span className={`text-base ${i < 3 ? 'text-accent-500' : 'text-background-300'}`}>
                      <i className="ri-arrow-up-line" aria-hidden="true" />
                    </span>
                  </Link>
                </li>
              ))}
            </ol>
          </div>
        </aside>
      </section>
    </>
  );
}